import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import RoutePolyline from '../../components/maps/RoutePolyline';
import { getGoogleApiFlag, trackGoogleApiUsage } from '../../hooks/useGoogleApiFlags';

// Phase 78bb — free haversine fallback used by the anchor-pin ETA when
// the legacy live-Directions flag is OFF.
function haversineKm(aLat, aLng, bLat, bLng) {
  if (!aLat || !aLng || !bLat || !bLng) return 0;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
import api from '../../lib/api';
import { ArrowClockwise, MagnifyingGlass, Funnel, ArrowsOut, X } from '@phosphor-icons/react';

// Speed → state mapping (Carloo-Tex Live Map parity)
//   driving   > 30 km/h   red
//   commuting 5-30        orange
//   on_move   1-5         green
//   stationary < 1        grey
const trailColor = (speed) => {
  const s = speed || 0;
  if (s > 30) return '#FF3333';    // Driving
  if (s >= 5) return '#FF8C00';    // Commuting
  if (s >= 1) return '#00C853';    // On the move
  return '#808080';                // Stationary
};

// Phase 76: dot color is the state color (Carloo-Tex). Border = white if
// fresh, dashed grey if offline (>10 min). Pilots and loadmen use the same
// speed-based palette; loadmen are distinguished by the "L" badge label.
const isStaleGps = (iso) => {
  if (!iso) return true;
  try {
    const ageMin = (Date.now() - new Date(iso).getTime()) / 60000;
    return ageMin > 10;
  } catch { return true; }
};

const stateLabel = (row) => {
  if (row.role === 'booker') return 'Trip';
  if (isStaleGps(row.last_gps_time)) return 'Offline';
  const s = row.speed || 0;
  if (s > 30) return 'Driving';
  if (s >= 5) return 'Commuting';
  if (s >= 1) return 'On the move';
  return 'Stationary';
};

const fmtAgo = (iso) => {
  if (!iso) return '—';
  try {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 10) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return '—'; }
};

// Phase 72: Idle >20 min rule — pilot on-duty with speed<1 and last_gps is
// older than 20 minutes (so we know they're genuinely parked vs. just slow
// bearings). Loadmen/bookers don't count.
const isIdleOver20Min = (row) => {
  if (row.role !== 'pilot') return false;
  if (row.status === 'Off Duty') return false;
  if ((row.speed || 0) >= 1) return false;
  if (!row.last_gps_time) return false;
  try {
    const ageMin = (Date.now() - new Date(row.last_gps_time).getTime()) / 60000;
    return ageMin > 20;
  } catch { return false; }
};

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

const INITIAL_CENTER = { lat: 11.1085, lng: 77.3411 }; // Tiruppur-ish default for Carloo

export default function LiveTrackingPage() {
  const { isLoaded } = useGoogleMaps();

  // Phase 78o — Live Tracking is pilot-only now. Loadmen removed from
  // the map so admins can stay focused on pilot routes & locations.
  const role = 'pilot';
  const [halt, setHalt] = useState('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);  // Phase 76b: hide offline (>10m)
  // Phase 78k — trip-marker filters
  const [pilotFilter, setPilotFilter] = useState('');   // empty = all pilots
  const [pickupFilter, setPickupFilter] = useState('');
  const [dropFilter, setDropFilter] = useState('');
  const [showTripMarkers, setShowTripMarkers] = useState(true);
  // Phase 78q — pin click → ETA + distance popover.
  // `selectedPin` shape: {kind, trip_id, supplier_name, lat, lng, pilot_name, eta_seconds, distance_meters, loading, error}
  const [selectedPin, setSelectedPin] = useState(null);
  // Phase 78r — selected-pilot trip details for the bottom panel.
  const [pilotTrips, setPilotTrips] = useState(null);
  const [data, setData] = useState({ rows: [], summary: {}, halt_options: [], trip_markers: [], pickup_options: [], drop_options: [] });
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);
  const centeredRef = useRef(false);

  const fetchFeed = useCallback(async () => {
    try {
      const { data: d } = await api.get('/admin/live-tracking', {
        params: {
          role,
          halt: halt || undefined,
          q: search.trim() || undefined,
          pilot_id: pilotFilter || undefined,
          pickup: pickupFilter.trim() || undefined,
          drop: dropFilter.trim() || undefined,
        },
      });
      setData(d);
      setLastRefreshed(new Date());
    } catch {} finally { setLoading(false); }
  }, [role, halt, search, pilotFilter, pickupFilter, dropFilter]);

  // Auto-refresh every 15s (user-requested cadence)
  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 15000);
    return () => clearInterval(id);
  }, [fetchFeed]);

  // Phase 78r — when a pilot is filter-selected, fetch his in-process
  // + allocated trips (with pooled children) for the bottom panel.
  // Reuses the pilot-trips-monitor endpoint that already returns
  // exactly this shape, in priority order.
  useEffect(() => {
    if (!pilotFilter) { setPilotTrips(null); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await api.get('/admin/pilot-trips-monitor', { params: { pilot_id: pilotFilter } });
        if (!cancelled) setPilotTrips({ in_process: d.in_process || [], allocated: d.allocated || [], pilot: d.pilot });
      } catch {
        if (!cancelled) setPilotTrips({ in_process: [], allocated: [], pilot: null });
      }
    };
    load();
    const id = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pilotFilter]);


  // Center map on first meaningful row
  useEffect(() => {
    if (centeredRef.current || !mapRef.current || data.rows.length === 0) return;
    const first = data.rows.find(r => r.lat && r.lng);
    if (!first) return;
    mapRef.current.panTo({ lat: first.lat, lng: first.lng });
    mapRef.current.setZoom(12);
    centeredRef.current = true;
  }, [data.rows]);

  // Phase 78o — Live Tracking is pilot-only now. Bookers + loadmen
  // hidden by design so the map stays focused on pilot routes.
  // "Active Only" still hides offline (>10m stale) pilots.
  const rows = (data.rows || [])
    .filter(r => r.role === 'pilot')
    .filter(r => !activeOnly || !isStaleGps(r.last_gps_time));
  const activeStaff = useMemo(() => rows, [rows]);

  // Phase 78u — When one pilot is selected, hide every other pilot's
  // dot + trail so the map shows only that pilot. Keeps the map
  // readable when there are 30-40 active pilots.
  const mapStaff = useMemo(
    () => (pilotFilter ? activeStaff.filter(r => r.id === pilotFilter) : activeStaff),
    [activeStaff, pilotFilter]
  );

  // Phase 78k — unique pilot list for the new pilot filter dropdown.
  const pilotOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of data.rows || []) {
      if (r.role !== 'pilot' || seen.has(r.id)) continue;
      seen.add(r.id);
      out.push({ id: r.id, name: r.name });
    }
    return out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [data.rows]);

  // Phase 78k — colour palette per kind. Same hue family for the three
  // P-D pairs so admins can spot "pickup vs drop" at a glance without
  // memorising 6 colours.
  // Phase 78p — Pending switched from yellow → dark navy per user feedback.
  const TRIP_KIND_COLOR = {
    IP: '#00C853', ID: '#00C853',           // 78m: green for in-progress (pooled too)
    AP: '#A855F7', AD: '#A855F7',           // 78m: only used as fallback; per-trip palette wins
    PP: '#0A1F44', PD: '#0A1F44',           // 78p: dark navy for pending (was yellow/grey)
  };
  // Phase 78m — per-allocated-trip colour cycle so each numbered trip
  // (2, 3, 4 …) has its own distinct route polyline + pin pair.
  const ALLOC_TRIP_PALETTE = ['#002FA7', '#A855F7', '#FF8C00', '#FF3399', '#00BFA5', '#D81B60', '#0288D1', '#7CB342'];
  const tripMarkers = data.trip_markers || [];

  // Phase 78m — build the per-trip polyline definitions so each in-progress /
  // allocated trip gets its own line on the map. Pooled stubs ride along on
  // the green colour of their parent in-progress lead.
  // Phase 78n — when a single pilot is filtered, also chain the trips
  // in priority order and draw a red-dotted "empty leg" polyline from
  // each drop to the next pickup so admin instantly sees where the
  // vehicle is going empty.
  const tripRoutes = useMemo(() => {
    const byTrip = {};
    for (const m of tripMarkers) {
      if (m.kind === 'PP' || m.kind === 'PD') continue;
      const slot = byTrip[m.trip_id] || (byTrip[m.trip_id] = { pickup: null, drop: null, seq: m.seq, kind: m.kind, parent_id: m.parent_id });
      if (m.kind === 'IP' || m.kind === 'AP') slot.pickup = { lat: m.lat, lng: m.lng };
      if (m.kind === 'ID' || m.kind === 'AD') slot.drop = { lat: m.lat, lng: m.lng };
    }
    const routes = [];
    Object.entries(byTrip).forEach(([tripId, info]) => {
      if (!info.pickup || !info.drop) return;
      const isAlloc = info.kind === 'AP' || info.kind === 'AD';
      const color = isAlloc
        ? ALLOC_TRIP_PALETTE[(info.seq - 2 + ALLOC_TRIP_PALETTE.length) % ALLOC_TRIP_PALETTE.length]
        : '#00C853';
      routes.push({ trip_id: tripId, color, seq: info.seq, kind: isAlloc ? 'allocated' : 'in_progress', parent_id: info.parent_id, path: [info.pickup, info.drop] });
    });

    // Phase 78n + 78o — Empty-leg ("deadhead") segments + km counters.
    // Only when a single pilot is in focus.
    const emptyLegs = [];
    let loadedKm = 0;
    let emptyKm = 0;
    const _hav = (a, b) => {
      if (!a || !b) return 0;
      const toRad = (x) => (x * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return 2 * 6371 * Math.asin(Math.sqrt(h));
    };
    if (pilotFilter) {
      const leads = routes
        .filter(r => !r.parent_id)               // exclude pooled stubs
        .sort((a, b) => (a.seq ?? 99) - (b.seq ?? 99) || a.trip_id.localeCompare(b.trip_id));
      // Loaded km — sum of every trip pickup→drop straight-line.
      for (const lead of leads) {
        loadedKm += _hav(lead.path[0], lead.path[1]);
      }
      // Empty km — sum of every drop → next pickup straight-line.
      for (let i = 0; i < leads.length - 1; i++) {
        const from = leads[i].path[1];
        const to = leads[i + 1].path[0];
        if (from && to) {
          emptyKm += _hav(from, to);
          emptyLegs.push({
            id: `empty-${leads[i].trip_id}-${leads[i + 1].trip_id}`,
            path: [from, to],
            from_trip: leads[i].trip_id,
            to_trip: leads[i + 1].trip_id,
          });
        }
      }
    }
    const totalKm = loadedKm + emptyKm;
    const emptyPct = totalKm > 0 ? Math.round((emptyKm / totalKm) * 100) : 0;

    return { routes, emptyLegs, emptyKm: Math.round(emptyKm * 10) / 10, totalKm: Math.round(totalKm * 10) / 10, emptyPct };
  }, [tripMarkers, pilotFilter]);

  const handleRowClick = (r) => {
    setSelectedId(r.id);
    // Phase 78l — clicking a pilot (on map OR in sidebar) auto-filters
    // the trip pins to just that pilot. Loadmen are not trip-owners so
    // we leave the filter alone for them.
    if (r.role === 'pilot') {
      setPilotFilter(r.id);
    }
    if (mapRef.current && r.lat && r.lng) {
      mapRef.current.panTo({ lat: r.lat, lng: r.lng });
      mapRef.current.setZoom(16);
    }
  };

  // Pooled children lookup for the bottom-panel display. The
  // pilot-trips-monitor endpoint already nests pool data via the
  // lead's `pooled_items[]` array, so we just expose it here.
  const poolItemsFor = (t) => Array.isArray(t.pooled_items) ? t.pooled_items : [];

  // Phase 78q — derive the GPS anchor for ETA calculations. When a
  // single pilot is filter-selected we use his current lat/lng; else
  // fall back to the row the admin most recently clicked in the
  // sidebar.
  const anchorPilot = useMemo(() => {
    const rs = data.rows || [];
    if (pilotFilter) return rs.find(r => r.id === pilotFilter && r.role === 'pilot') || null;
    if (selectedId) return rs.find(r => r.id === selectedId && r.role === 'pilot') || null;
    return null;
  }, [data.rows, pilotFilter, selectedId]);

  // Phase 78q — pin click → fetch Routes-API ETA + distance from
  // anchor pilot's current GPS to the clicked pin via DirectionsService.
  // Phase 78u — if the clicked pin belongs to a pilot (IP/ID/AP/AD), also
  // auto-filter the map to that pilot so admin instantly sees the
  // pilot's complete pickup/drop route stack with Google road polylines.
  const handlePinClick = useCallback((m) => {
    if (m.pilot_id && m.pilot_id !== pilotFilter) {
      setPilotFilter(m.pilot_id);
    }
    if (mapRef.current && m.lat && m.lng) {
      mapRef.current.panTo({ lat: m.lat, lng: m.lng });
    }
    setSelectedPin({
      kind: m.kind, trip_id: m.trip_id, supplier_name: m.supplier_name,
      lat: m.lat, lng: m.lng, pilot_name: m.pilot_name,
      anchor_name: anchorPilot?.name,
      loading: !!anchorPilot,
      eta_seconds: null, distance_meters: null,
      eta_text: null, distance_text: null,
      error: anchorPilot ? null : 'Select a pilot first to compute ETA',
    });
    if (!anchorPilot || !window.google?.maps?.DirectionsService) return;
    // Phase 78bb — default path: free haversine ETA (no Directions call).
    // The legacy `eta_admin_live_tracking_anchor` flag lets the admin force
    // a live Google call back on if they need road-route accuracy.
    if (!getGoogleApiFlag('eta_admin_live_tracking_anchor')) {
      const dKm = haversineKm(anchorPilot.lat, anchorPilot.lng, m.lat, m.lng);
      const etaMin = Math.max(1, Math.round((dKm / 35) * 60));
      setSelectedPin(p => (p && p.trip_id === m.trip_id && p.kind === m.kind) ? {
        ...p, loading: false,
        eta_seconds: etaMin * 60,
        distance_meters: dKm * 1000,
        eta_text: `${etaMin} min`,
        distance_text: `${dKm.toFixed(1)} km`,
        source: 'haversine',
      } : p);
      return;
    }
    trackGoogleApiUsage('eta_admin_live_tracking_anchor', 1);
    const ds = new window.google.maps.DirectionsService();
    ds.route({
      origin: { lat: anchorPilot.lat, lng: anchorPilot.lng },
      destination: { lat: m.lat, lng: m.lng },
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === 'OK' && result.routes?.[0]?.legs?.[0]) {
        const leg = result.routes[0].legs[0];
        setSelectedPin(p => (p && p.trip_id === m.trip_id && p.kind === m.kind) ? {
          ...p, loading: false,
          eta_seconds: leg.duration?.value || 0,
          distance_meters: leg.distance?.value || 0,
          eta_text: leg.duration?.text || '—',
          distance_text: leg.distance?.text || '—',
        } : p);
      } else {
        setSelectedPin(p => (p && p.trip_id === m.trip_id) ? { ...p, loading: false, error: `Routes API: ${status}` } : p);
      }
    });
  }, [anchorPilot, pilotFilter]);


  return (
    <div data-testid="live-tracking-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00C853] animate-pulse" /> Live Staff Locations
          </h1>
          <p className="text-[#666] text-xs mt-1">
            {data.summary?.total ?? 0} live · auto-refresh 15s ·{' '}
            {lastRefreshed ? `last refreshed ${fmtAgo(lastRefreshed.toISOString())}` : '—'}
          </p>
        </div>
        <button
          onClick={fetchFeed}
          disabled={loading}
          className="bg-[#002FA7] text-white rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-[#002FA7]/90 disabled:opacity-50"
          data-testid="live-refresh-btn"
        >
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E5E5] p-3 flex items-center gap-2 flex-wrap" data-testid="live-filter-bar">
        <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#666] px-1">
          <Funnel size={14} /> Filters
        </div>
        <select
          value={halt}
          onChange={e => setHalt(e.target.value)}
          className="border border-[#E5E5E5] rounded px-2 py-1.5 text-sm bg-white"
          data-testid="live-halt-filter"
        >
          <option value="">All Halt Locations</option>
          {data.halt_options?.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, mobile…"
            className="w-full border border-[#E5E5E5] rounded pl-8 pr-2 py-1.5 text-sm"
            data-testid="live-search-input"
          />
        </div>
        <button
          type="button"
          onClick={() => setActiveOnly(v => !v)}
          className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border transition-colors ${
            activeOnly
              ? 'bg-[#00C853] text-white border-[#00C853]'
              : 'bg-white text-[#666] border-[#E5E5E5] hover:border-[#00C853] hover:text-[#00C853]'
          }`}
          data-testid="live-active-only-toggle"
          title="Hide pilots/loadmen with no GPS in the last 10 minutes"
        >
          {activeOnly ? '● Active Only' : '○ Active Only'}
        </button>

        {/* Phase 78k — trip-marker filter row */}
        <div className="basis-full flex items-center gap-2 flex-wrap pt-2 border-t border-[#F3F3F3] mt-1">
          <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#666] px-1">
            Trips
          </div>
          <select
            value={pilotFilter}
            onChange={e => setPilotFilter(e.target.value)}
            className="border border-[#E5E5E5] rounded px-2 py-1.5 text-sm bg-white"
            data-testid="live-pilot-filter"
          >
            <option value="">All Pilots</option>
            {pilotOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {pilotFilter && tripRoutes.totalKm > 0 && (
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-full ${
                tripRoutes.emptyPct >= 30
                  ? 'bg-[#FF3333]/10 text-[#FF3333]'
                  : tripRoutes.emptyPct >= 15
                    ? 'bg-[#FF8C00]/10 text-[#FF8C00]'
                    : 'bg-[#00C853]/10 text-[#00C853]'
              }`}
              title={`Empty (deadhead) ${tripRoutes.emptyKm} km / total ${tripRoutes.totalKm} km · straight-line approximation`}
              data-testid="live-empty-km-chip"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-[#FF3333]" />
              Empty: {tripRoutes.emptyKm} km · {tripRoutes.emptyPct}%
            </span>
          )}
          <input
            type="text"
            value={pickupFilter}
            onChange={e => setPickupFilter(e.target.value)}
            placeholder="Pickup location…"
            list="live-pickup-options"
            className="border border-[#E5E5E5] rounded px-2 py-1.5 text-sm min-w-[160px]"
            data-testid="live-pickup-filter"
          />
          <datalist id="live-pickup-options">
            {(data.pickup_options || []).map(s => <option key={s} value={s} />)}
          </datalist>
          <input
            type="text"
            value={dropFilter}
            onChange={e => setDropFilter(e.target.value)}
            placeholder="Drop location…"
            list="live-drop-options"
            className="border border-[#E5E5E5] rounded px-2 py-1.5 text-sm min-w-[160px]"
            data-testid="live-drop-filter"
          />
          <datalist id="live-drop-options">
            {(data.drop_options || []).map(s => <option key={s} value={s} />)}
          </datalist>
          <button
            type="button"
            onClick={() => setShowTripMarkers(v => !v)}
            className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border transition-colors ${
              showTripMarkers
                ? 'bg-[#002FA7] text-white border-[#002FA7]'
                : 'bg-white text-[#666] border-[#E5E5E5] hover:border-[#002FA7] hover:text-[#002FA7]'
            }`}
            data-testid="live-trip-markers-toggle"
            title="Show / hide pickup-drop pins"
          >
            {showTripMarkers ? `● Trip Pins (${tripMarkers.length})` : '○ Trip Pins'}
          </button>
          {(pilotFilter || pickupFilter || dropFilter) && (
            <button
              type="button"
              onClick={() => { setPilotFilter(''); setPickupFilter(''); setDropFilter(''); }}
              className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border border-[#E5E5E5] text-[#666] hover:bg-[#F3F3F3]"
              data-testid="live-trip-filters-clear"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Speed legend */}
      <div className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-widest text-[#666] flex-wrap px-1" data-testid="live-speed-legend">
        <span>Trail Speed:</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#FF3333' }} /> Driving (&gt;30 km/h)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#FF8C00' }} /> Commuting (5-30)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#00C853' }} /> On the move (1-5)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#808080' }} /> Stationary (&lt;1)</span>
      </div>

      {/* Phase 78k — trip-kind legend */}
      {showTripMarkers && tripMarkers.length > 0 && (
        <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-[#666] flex-wrap px-1" data-testid="live-trip-legend">
          <span>Trip Pins:</span>
          <span className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[8px] font-black text-white" style={{ background: '#00C853' }}>IP</span>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[8px] font-black text-white" style={{ background: '#00C853' }}>ID</span>
            In-Progress (green)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[7px] font-black text-white" style={{ background: '#002FA7' }}>2AP</span>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[7px] font-black text-white" style={{ background: '#A855F7' }}>3AP</span>
            Allocated (numbered, per-trip colour)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[8px] font-black text-white" style={{ background: '#FFD700' }}>PP</span>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[8px] font-black text-white" style={{ background: '#808080' }}>PD</span>
            Pending (un-allocated)
          </span>
          {pilotFilter && (
            <span className="flex items-center gap-1" data-testid="live-empty-leg-legend">
              <span className="inline-block w-8 h-1" style={{ backgroundImage: 'linear-gradient(to right, #FF3333 50%, transparent 0)', backgroundSize: '6px 1px', backgroundRepeat: 'repeat-x' }} />
              Empty Leg (drop → next pickup)
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {/* Map */}
        <div className="relative bg-white border border-[#E5E5E5] overflow-hidden" style={{ height: '640px' }}>
          {/* Map / Satellite toggle */}
          <div className="absolute top-2 left-2 z-10 flex border border-[#E5E5E5] bg-white shadow-sm text-[11px] font-bold uppercase tracking-widest">
            {['roadmap', 'satellite'].map(t => (
              <button
                key={t}
                onClick={() => setMapType(t)}
                className={`px-3 py-1.5 ${mapType === t ? 'bg-[#002FA7] text-white' : 'bg-white text-[#666] hover:bg-[#F3F3F3]'}`}
                data-testid={`live-map-${t}`}
              >
                {t === 'roadmap' ? 'Map' : 'Satellite'}
              </button>
            ))}
          </div>

          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={INITIAL_CENTER}
              zoom={11}
              mapTypeId={mapType}
              options={{
                styles: mapType === 'roadmap' ? MAP_STYLES : undefined,
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                mapId: GOOGLE_MAPS_MAP_ID,
              }}
              onLoad={(m) => { mapRef.current = m; }}
            >
              {/* Staff trail polylines — Phase 78u: only the selected
                  pilot's trail when one is picked, else all. */}
              {mapStaff.map(r => r.trail && r.trail.length >= 2 && (
                <Polyline
                  key={`trail-${r.id}`}
                  path={r.trail.map(p => ({ lat: p.lat, lng: p.lng }))}
                  options={{
                    strokeColor: trailColor(r.speed),
                    strokeOpacity: 0.75,
                    strokeWeight: 4,
                    geodesic: false,
                  }}
                />
              ))}

              {/* Staff markers — Phase 78o: pilots only (loadmen filtered
                  out). Dot colour reflects current speed/state; offline
                  (>10m) shows dashed grey ring.
                  Phase 78u — only the selected pilot when filtered. */}
              {mapStaff.map(r => {
                const stale = isStaleGps(r.last_gps_time);
                const dotColor = stale ? '#808080' : trailColor(r.speed);
                const ringColor = stale ? '#FF3333' : '#FFFFFF';
                const ringStyle = stale ? `border:3px dashed ${ringColor}` : `border:3px solid ${ringColor};box-shadow:0 0 0 2px ${dotColor},0 2px 6px rgba(0,0,0,.35)`;
                return (
                  <AdvancedMarker
                    key={`marker-${r.role}-${r.id}`}
                    position={{ lat: r.lat, lng: r.lng }}
                    onClick={() => handleRowClick(r)}
                    contentHTML={`
                      <div style="display:flex;flex-direction:column;align-items:center;gap:2px;font-family:system-ui,sans-serif;transform:translateY(-4px)">
                        <div style="background:rgba(255,255,255,.92);color:#0A0A0A;padding:1px 6px;border-radius:8px;font-size:10px;font-weight:700;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,.2);max-width:140px;overflow:hidden;text-overflow:ellipsis">${(r.name || '').replace(/[<>"']/g, '')}${stale ? ' · OFFLINE' : ''}</div>
                        <div style="width:32px;height:32px;border-radius:50%;background:${dotColor};${ringStyle};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:13px">${(r.name || '?').trim().charAt(0).toUpperCase()}</div>
                      </div>
                    `}
                    title={`${r.name} · ${stateLabel(r)} · ${r.speed || 0} km/h`}
                  />
                );
              })}

              {/* Phase 78k — Trip pickup/drop markers (IP/ID/AP/AD/PP/PD). */}
              {/* Phase 78m — Route polylines: one per trip. Green for in-progress
                  (incl. pooled children); allocated trips cycle through a
                  distinct colour palette by their priority sequence. */}
              {/* Phase 78p — When a pilot is selected, fetch real
                  Google road-route polylines + ETA. For "All Pilots"
                  view we stick with the cheap straight-line polylines
                  to avoid 50+ Routes API calls per refresh. */}
              {showTripMarkers && tripRoutes.routes.map(r => (
                pilotFilter ? (
                  <RoutePolyline
                    key={`route-${r.trip_id}`}
                    origin={r.path[0]}
                    destination={r.path[1]}
                    usageKey="polyline_admin_live_tracking"
                    options={{
                      strokeColor: r.color,
                      strokeOpacity: r.kind === 'in_progress' ? 0.9 : 0.78,
                      strokeWeight: 5,
                      zIndex: r.kind === 'in_progress' ? 5 : 4,
                    }}
                    onRoute={(info) => {
                      // Stash ETA + km on the routes-by-id map so the
                      // sidebar can show them; we re-use the global
                      // tripRoutes object via a side cache keyed by
                      // trip_id (safe because RoutePolyline calls
                      // onRoute exactly once per origin/dest pair).
                      if (typeof window !== 'undefined') {
                        window.__tripRouteEtaCache = window.__tripRouteEtaCache || {};
                        window.__tripRouteEtaCache[r.trip_id] = info;
                      }
                    }}
                  />
                ) : (
                  <Polyline
                    key={`route-${r.trip_id}`}
                    path={r.path}
                    options={{
                      strokeColor: r.color,
                      strokeOpacity: r.kind === 'in_progress' ? 0.85 : 0.7,
                      strokeWeight: 4,
                      geodesic: false,
                      icons: r.kind === 'allocated' ? [{
                        icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                        offset: '0',
                        repeat: '16px',
                      }] : undefined,
                      zIndex: r.kind === 'in_progress' ? 5 : 4,
                    }}
                  />
                )
              ))}

              {/* Phase 78n + 78p — Empty-leg ("deadhead") polylines.
                  Now uses Google Routes API for road-following dotted
                  red lines so admin sees actual driving distance.
                  Only rendered when one pilot is filter-selected. */}
              {showTripMarkers && tripRoutes.emptyLegs.map(leg => (
                <RoutePolyline
                  key={leg.id}
                  origin={leg.path[0]}
                  destination={leg.path[1]}
                  usageKey="polyline_admin_live_tracking"
                  options={{
                    strokeColor: '#FF3333',
                    strokeOpacity: 0,
                    strokeWeight: 3,
                    icons: [{
                      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#FF3333', scale: 2 },
                      offset: '0',
                      repeat: '10px',
                    }],
                    zIndex: 3,
                  }}
                />
              ))}

              {showTripMarkers && tripMarkers.map(m => {
                // Phase 78m — Per-trip colour for allocated rings (so the
                // pin matches its route polyline). Pending stays grey/gold.
                const isAlloc = m.kind === 'AP' || m.kind === 'AD';
                const palettedColor = isAlloc
                  ? ALLOC_TRIP_PALETTE[(m.seq - 2 + ALLOC_TRIP_PALETTE.length) % ALLOC_TRIP_PALETTE.length]
                  : TRIP_KIND_COLOR[m.kind] || '#0A0A0A';
                const color = palettedColor;
                const label = (m.supplier_name || '').replace(/[<>"']/g, '').slice(0, 28);
                const shape = m.kind.endsWith('P')
                  ? `border-radius:4px`            // Pickup → square
                  : `border-radius:50% 50% 50% 0;transform:rotate(-45deg)`;  // Drop → teardrop
                // Phase 78m — prefix the kind with the seq number for
                // allocated trips so admin can read the journey order at
                // a glance (2AP / 2AD, 3AP / 3AD, …).
                const seqPrefix = (isAlloc && m.seq && m.seq >= 2) ? String(m.seq) : '';
                const displayKind = `${seqPrefix}${m.kind}`;
                // Phase 78p — enlarge pin labels +20% so they're easier to read
                // on a busy map. Inner font 8→10px, outer label 9→11px,
                // marker tile 24→29px, max label width 160→192px.
                const innerLabel = `<span style="${m.kind.endsWith('P') ? '' : 'transform:rotate(45deg);display:inline-block;'}font-size:10px;font-weight:900">${displayKind}</span>`;
                return (
                  <AdvancedMarker
                    key={`trip-${m.kind}-${m.trip_id}`}
                    position={{ lat: m.lat, lng: m.lng }}
                    onClick={() => handlePinClick(m)}
                    title={`${displayKind} — ${m.supplier_name || ''} · #${m.trip_id} · ${m.pilot_name || 'unassigned'}`}
                    contentHTML={`
                      <div style="display:flex;flex-direction:column;align-items:center;gap:1px;font-family:system-ui,sans-serif;transform:translateY(-4px);pointer-events:auto">
                        <div style="background:rgba(255,255,255,.95);color:${color};padding:1px 6px;font-size:11px;font-weight:800;white-space:nowrap;border:1px solid ${color};max-width:192px;overflow:hidden;text-overflow:ellipsis">${displayKind} ${label}</div>
                        <div style="width:29px;height:29px;background:${color};${shape};color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,.3)">${innerLabel}</div>
                      </div>
                    `}
                  />
                );
              })}
            </GoogleMap>
          ) : (
            <div className="h-full flex items-center justify-center text-[#666] text-sm">Loading map…</div>
          )}

          {/* Phase 78q — Trip pin ETA popover. Anchored to the
              top-right of the map container so it's always visible
              regardless of where on the map the pilot is. */}
          {selectedPin && (
            <div className="absolute top-12 right-3 bg-white border border-[#E5E5E5] shadow-lg w-72 z-20" data-testid="live-pin-popover">
              <div className="px-3 py-2 bg-[#0A0A0A] text-white flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest font-bold">
                  {selectedPin.kind} · #{selectedPin.trip_id}
                </p>
                <button
                  onClick={() => setSelectedPin(null)}
                  className="text-white/70 hover:text-white"
                  data-testid="live-pin-popover-close"
                ><X size={14} weight="bold" /></button>
              </div>
              <div className="px-3 py-3 space-y-2">
                <p className="text-sm font-bold leading-tight">{selectedPin.supplier_name || '—'}</p>
                {selectedPin.pilot_name && (
                  <p className="text-[11px] text-[#666]">Assigned to <b className="text-[#0A0A0A]">{selectedPin.pilot_name}</b></p>
                )}
                {selectedPin.error && (
                  <p className="text-[11px] text-[#FF8C00] font-bold" data-testid="live-pin-popover-error">{selectedPin.error}</p>
                )}
                {selectedPin.loading && (
                  <p className="text-[11px] text-[#666]" data-testid="live-pin-popover-loading">Computing ETA…</p>
                )}
                {!selectedPin.loading && selectedPin.eta_text && (
                  <div className="grid grid-cols-2 gap-2 pt-1" data-testid="live-pin-popover-eta">
                    <div className="bg-[#00C853]/10 p-2 text-center">
                      <p className="text-base font-black text-[#00C853]">{selectedPin.eta_text}</p>
                      <p className="text-[9px] uppercase tracking-widest text-[#666] font-bold">ETA from {selectedPin.anchor_name}</p>
                    </div>
                    <div className="bg-[#002FA7]/10 p-2 text-center">
                      <p className="text-base font-black text-[#002FA7]">{selectedPin.distance_text}</p>
                      <p className="text-[9px] uppercase tracking-widest text-[#666] font-bold">Driving Distance</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phase 78u — Right Active Staff panel removed by user request.
            The map is now the single source of truth for who is where;
            pilot selection happens via the Pilot filter dropdown above. */}
      </div>

      {/* Phase 78r — Bottom panel: trip details of selected pilot
          (replaces the old all-pilot summary tiles). Shows In-Process
          first (live trips), then Allocated in priority order. Pooled
          children are embedded under their lead so admin sees every
          stop the pilot has in hand. */}
      {!pilotFilter ? (
        <div className="text-center text-[#666] text-sm py-6 border border-dashed border-[#E5E5E5] bg-white" data-testid="live-trips-empty">
          Pick a pilot in the filter above to see his trip details here.
        </div>
      ) : !pilotTrips ? (
        <div className="text-center text-[#666] text-sm py-6">Loading pilot trips…</div>
      ) : (
        <div className="bg-white border border-[#E5E5E5]" data-testid="live-pilot-trips-panel">
          <div className="px-3 py-2 border-b border-[#E5E5E5] bg-[#F7F7F7] flex items-center gap-2 flex-wrap">
            <h2 className="text-sm uppercase tracking-widest font-bold">Trip Details — {pilotTrips.pilot?.name || '—'}</h2>
            <span className="text-xs text-[#666]">({pilotTrips.in_process.length} live · {pilotTrips.allocated.length} queued)</span>
            {pilotTrips.pilot?.priority_lock && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold bg-[#FF8C00]/10 text-[#FF8C00]">Priority Locked</span>
            )}
          </div>
          <div className="divide-y divide-[#F0F0F0]">
            {[...pilotTrips.in_process, ...pilotTrips.allocated].length === 0 && (
              <p className="text-center text-[#999] text-sm py-6">No live or queued trips.</p>
            )}
            {/* In-process first */}
            {pilotTrips.in_process.map(t => (
              <LiveTripRow key={t.id} t={t} kindLabel="LIVE" kindCls="bg-[#00C853] text-white" pooled={poolItemsFor(t)} />
            ))}
            {/* Allocated in priority order */}
            {pilotTrips.allocated.map((t, i) => (
              <LiveTripRow key={t.id} t={t} kindLabel={`#${i + 1}`} kindCls="bg-[#002FA7] text-white" pooled={poolItemsFor(t)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Phase 78r — single trip row used by the bottom panel.
function LiveTripRow({ t, kindLabel, kindCls, pooled }) {
  const items = Array.isArray(t.items) && t.items.length ? t.items : (t.item ? [{ name: t.item, qty: t.qty, uom: t.uom }] : []);
  return (
    <div className="px-3 py-2" data-testid={`live-trip-row-${t.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold ${kindCls}`}>{kindLabel}</span>
        <span className="font-mono text-xs font-bold">#{t.id}</span>
        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold bg-[#0A0A0A] text-white">{t.status?.replace(/_/g, ' ')}</span>
        {t.priority && t.priority !== 'others' && (
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold bg-[#FF3333]/10 text-[#FF3333]">{t.priority}</span>
        )}
      </div>
      <p className="text-sm font-bold leading-tight mt-1">
        {t.pickup_supplier_name || '-'} <span className="text-[#666] font-normal">→</span> {t.drop_supplier_name || '-'}
      </p>
      <div className="text-[11px] text-[#666] mt-0.5 flex items-center gap-3 flex-wrap">
        {items.length > 0 && <span>📦 {items.filter(i => i.name).map(i => `${i.name} ${i.qty || ''}${i.uom || ''}`).join(' · ')}</span>}
        {t.booked_by_name && <span>by <b className="text-[#0A0A0A]">{t.booked_by_name}</b>{t.booked_by_mobile ? ` · ${t.booked_by_mobile}` : ''}</span>}
      </div>
      {pooled.length > 0 && (
        <div className="border-l-4 border-[#A855F7] bg-[#A855F7]/5 pl-2 mt-1.5 py-1 space-y-0.5" data-testid={`live-trip-pooled-${t.id}`}>
          <p className="text-[9px] uppercase tracking-widest text-[#A855F7] font-bold">+{pooled.length} Pooled with this trip</p>
          {pooled.map((c, i) => (
            <div key={c.id || c.booking_id || i} className="text-[11px]">
              {c.booking_id && <span className="font-mono text-[10px] text-[#666] mr-1">#{c.booking_id}</span>}
              <span className="font-bold">{c.item || '-'}</span>
              <span className="text-[#666]"> {c.qty || ''}{c.uom || ''}</span>
              {c.booked_by_name && <span className="text-[#666]"> · by {c.booked_by_name}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
